import { useMemo, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useDroppable,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import TaskCard, { type TaskCardProps } from './TaskCard';
import type { Task } from '../types/task';

// SortableTaskCard wraps TaskCard with dnd-kit's useSortable hook.
// Only the in-lane instance of a task registers as a draggable — the
// DragOverlay uses plain TaskCard directly so it does not register a
// duplicate draggable node (which would corrupt the source card's
// registration when the overlay unmounts).
function SortableTaskCard(props: TaskCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: props.task.TaskID });
  return (
    <TaskCard
      {...props}
      rootRef={setNodeRef}
      rootAttributes={attributes}
      rootListeners={listeners}
      rootStyle={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0 : 1,
        cursor: 'grab',
      }}
    />
  );
}

interface SwimLanesProps {
  tasks: Task[];
  defaultShortName: string;
  onTaskChanged: (updated: Task) => void;
  onError: (message: string) => void;
  onWorkstreamChanged: () => void;
  onCreateTask: () => Promise<void>;
  onReorderTask: (
    taskID: string,
    anchor: { kind: 'after' | 'before'; targetTaskID: string },
  ) => Promise<void>;
  onChangeTaskState: (taskID: string, newState: string) => Promise<void>;
}

// Each lane corresponds to one of the four task states defined by the
// backend. The `state` field here is the canonical value to write when a
// task is dropped into that lane.
const LANES = [
  { name: 'Pending', state: 'Pending', matches: ['Pending', ''] },
  { name: 'Executing', state: 'Executing', matches: ['Executing'] },
  { name: 'Review', state: 'Awaiting Code Review', matches: ['Awaiting Code Review'] },
  { name: 'Completed', state: 'Completed', matches: ['Completed'] },
] as const;

// Droppable id prefix so lane container droppables can be distinguished
// from task sortable ids (which are raw UUIDs).
const LANE_DROPPABLE_PREFIX = 'lane:';
const laneDroppableID = (laneName: string) => `${LANE_DROPPABLE_PREFIX}${laneName}`;
const isLaneDroppable = (id: string) => id.startsWith(LANE_DROPPABLE_PREFIX);
const laneNameFromDroppable = (id: string) => id.slice(LANE_DROPPABLE_PREFIX.length);

function PlusIcon({ size = 14 }: { size?: number }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="#0969da"
      stroke="none"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="16" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" />
      <line x1="8" y1="12" x2="16" y2="12" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}

function NewTaskButton({ onClick }: { onClick: () => Promise<void> }) {
  const [creating, setCreating] = useState(false);
  return (
    <button
      type="button"
      disabled={creating}
      onClick={async () => {
        if (creating) return;
        setCreating(true);
        try {
          await onClick();
        } finally {
          setCreating(false);
        }
      }}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '6px 8px',
        marginTop: '4px',
        background: 'none',
        border: 'none',
        cursor: creating ? 'wait' : 'pointer',
        color: '#0969da',
        fontSize: '13px',
        fontWeight: 500,
        width: '100%',
        textAlign: 'left',
        borderRadius: '4px',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = '#f0f6ff';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = 'transparent';
      }}
    >
      <PlusIcon />
      <span>New Task</span>
    </button>
  );
}

// A single swim lane. Each lane registers itself as a droppable so the
// user can drop tasks on an empty lane or on its whitespace. When the lane
// is the current drop target (target state), the background is tinted
// light blue to indicate the task will land here on release.
interface LaneColumnProps {
  lane: (typeof LANES)[number];
  laneTasks: Task[];
  isTargetLane: boolean;
  isSourceLane: boolean;
  defaultShortName: string;
  onTaskChanged: (updated: Task) => void;
  onError: (message: string) => void;
  onWorkstreamChanged: () => void;
  onCreateTask: () => Promise<void>;
  children?: React.ReactNode;
}

function LaneColumn({
  lane,
  laneTasks,
  isTargetLane,
  isSourceLane,
  defaultShortName,
  onTaskChanged,
  onError,
  onWorkstreamChanged,
  onCreateTask,
}: LaneColumnProps) {
  const { setNodeRef } = useDroppable({ id: laneDroppableID(lane.name) });
  const isPending = lane.name === 'Pending';

  // Highlight the lane background only when it is the target of an
  // active cross-lane drag (i.e. the user is hovering this lane while
  // dragging a card that originated in a different lane).
  const highlightBg = isTargetLane && !isSourceLane ? '#dbeafe' : undefined;

  return (
    <div
      ref={setNodeRef}
      style={{
        border: '1px solid #ccc',
        borderRadius: '8px',
        overflow: 'hidden',
        minHeight: '300px',
        display: 'flex',
        flexDirection: 'column',
        transition: 'background-color 0.15s',
      }}
    >
      <div
        style={{
          padding: '8px 12px',
          fontWeight: 600,
          fontSize: '14px',
          backgroundColor: '#dbeafe',
          borderBottom: '1px solid #ccc',
        }}
      >
        {lane.name}
      </div>
      <div
        style={{
          flex: 1,
          padding: '8px',
          backgroundColor: highlightBg,
          transition: 'background-color 0.15s',
        }}
      >
        <SortableContext
          id={lane.name}
          items={laneTasks.map((t) => t.TaskID)}
          strategy={verticalListSortingStrategy}
        >
          {laneTasks.map((task) => (
            <SortableTaskCard
              key={task.TaskID}
              task={task}
              defaultShortName={defaultShortName}
              onTaskChanged={onTaskChanged}
              onError={onError}
              onWorkstreamChanged={onWorkstreamChanged}
            />
          ))}
        </SortableContext>
        {isPending && <NewTaskButton onClick={onCreateTask} />}
      </div>
    </div>
  );
}

export default function SwimLanes({
  tasks,
  defaultShortName,
  onTaskChanged,
  onError,
  onWorkstreamChanged,
  onCreateTask,
  onReorderTask,
  onChangeTaskState,
}: SwimLanesProps) {
  const [activeID, setActiveID] = useState<string | null>(null);
  // The lane currently being hovered during a drag. Set via onDragOver,
  // cleared on drag end/cancel. Used to highlight the target lane.
  const [targetLane, setTargetLane] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      // Require a small drag distance before initiating, so simple
      // clicks on the card (e.g. opening the assignee menu) are not
      // mistaken for drags.
      activationConstraint: { distance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Group tasks by lane (in their current rank order).
  const tasksByLane = useMemo(() => {
    const grouped: Record<string, Task[]> = {};
    for (const lane of LANES) {
      grouped[lane.name] = tasks.filter((t) =>
        (lane.matches as readonly string[]).includes(t.State ?? ''),
      );
    }
    return grouped;
  }, [tasks]);

  // Find which lane a task belongs to by ID.
  const findLane = (taskID: string): string | null => {
    for (const lane of LANES) {
      if (tasksByLane[lane.name].some((t) => t.TaskID === taskID)) {
        return lane.name;
      }
    }
    return null;
  };

  const activeTask = activeID ? tasks.find((t) => t.TaskID === activeID) ?? null : null;
  const sourceLane = activeID ? findLane(activeID) : null;

  // Custom collision detection: implements the spec rule "the majority of
  // the task card is within the bounds of the swimlane" for target-lane
  // selection. When the dragged card overlaps a different lane by more
  // than 50% of its area, the lane wins. Otherwise we fall back to
  // rectIntersection on the item droppables so vertical reordering inside
  // the source lane continues to work.
  const collisionDetection: CollisionDetection = (args) => {
    const { droppableContainers, active, collisionRect } = args;

    // Partition droppables into lane containers and task items.
    const laneContainers = droppableContainers.filter((c) =>
      isLaneDroppable(String(c.id)),
    );
    const itemContainers = droppableContainers.filter(
      (c) => !isLaneDroppable(String(c.id)),
    );

    const activeLane = findLane(String(active.id));
    const dragArea = collisionRect.width * collisionRect.height;

    // First, check each non-source lane to see if the dragged card
    // overlaps it by >= 50% of its area. If so, that lane wins.
    if (activeLane && dragArea > 0) {
      let bestLaneID: string | null = null;
      let bestLaneIntersection = 0;
      for (const lane of laneContainers) {
        const laneName = laneNameFromDroppable(String(lane.id));
        if (laneName === activeLane) continue;
        const rect = lane.rect.current;
        if (!rect) continue;
        const overlapX = Math.max(
          0,
          Math.min(collisionRect.right, rect.right) - Math.max(collisionRect.left, rect.left),
        );
        const overlapY = Math.max(
          0,
          Math.min(collisionRect.bottom, rect.bottom) - Math.max(collisionRect.top, rect.top),
        );
        const overlap = overlapX * overlapY;
        if (overlap / dragArea >= 0.5 && overlap > bestLaneIntersection) {
          bestLaneIntersection = overlap;
          bestLaneID = String(lane.id);
        }
      }
      if (bestLaneID) {
        const laneContainer = laneContainers.find((c) => String(c.id) === bestLaneID);
        return laneContainer ? [{ id: bestLaneID, data: { droppableContainer: laneContainer, value: bestLaneIntersection } }] : [];
      }
    }

    // Otherwise, fall back to closestCenter on task items (vertical
    // reorder within the source lane). closestCenter always returns
    // a target if any item exists, so releasing in whitespace below
    // the last card still lands on the nearest item — matching the
    // original behavior before cross-lane support was added.
    return closestCenter({
      ...args,
      droppableContainers: itemContainers,
    });
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveID(String(event.active.id));
    setTargetLane(null);
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { over, active } = event;
    if (!over) {
      setTargetLane(null);
      return;
    }
    const overID = String(over.id);
    const activeLane = findLane(String(active.id));
    if (!activeLane) {
      setTargetLane(null);
      return;
    }
    // If over a lane droppable, that lane is the target.
    if (isLaneDroppable(overID)) {
      const lane = laneNameFromDroppable(overID);
      setTargetLane(lane !== activeLane ? lane : null);
      return;
    }
    // Over a task: no target lane (we're doing a vertical reorder
    // within the source lane; tasks in other lanes do not become
    // targets via hovering over a task in them because the custom
    // collision detection would have picked the lane instead).
    setTargetLane(null);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveID(null);
    setTargetLane(null);
    if (!over) return;

    const activeIDStr = String(active.id);
    const overIDStr = String(over.id);

    const srcLane = findLane(activeIDStr);
    if (!srcLane) return;

    // Cross-lane drop: the collision function picked a lane droppable.
    // Change the task's state but leave its rank untouched.
    if (isLaneDroppable(overIDStr)) {
      const dstLaneName = laneNameFromDroppable(overIDStr);
      if (dstLaneName === srcLane) return;
      const dstLane = LANES.find((l) => l.name === dstLaneName);
      if (!dstLane) return;
      onChangeTaskState(activeIDStr, dstLane.state).catch(() => {
        // Errors are surfaced via the onError pipeline inside the parent.
      });
      return;
    }

    // Same-lane vertical reorder.
    if (active.id === over.id) return;
    const dstLane = findLane(overIDStr);
    if (!dstLane || srcLane !== dstLane) return;

    const laneTasks = tasksByLane[srcLane];
    const oldIndex = laneTasks.findIndex((t) => t.TaskID === activeIDStr);
    const overIndex = laneTasks.findIndex((t) => t.TaskID === overIDStr);
    if (oldIndex === -1 || overIndex === -1) return;

    let anchor: { kind: 'after' | 'before'; targetTaskID: string };
    if (overIndex === 0 && oldIndex !== 0) {
      anchor = { kind: 'before', targetTaskID: laneTasks[0].TaskID };
    } else if (oldIndex < overIndex) {
      anchor = { kind: 'after', targetTaskID: laneTasks[overIndex].TaskID };
    } else {
      anchor = { kind: 'after', targetTaskID: laneTasks[overIndex - 1].TaskID };
    }

    onReorderTask(activeIDStr, anchor).catch(() => {
      // Errors are surfaced via the onError pipeline inside the parent.
    });
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={() => {
        setActiveID(null);
        setTargetLane(null);
      }}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, minmax(220px, 1fr))',
          gap: '12px',
          marginTop: '16px',
          overflowX: 'auto',
        }}
      >
        {LANES.map((lane) => (
          <LaneColumn
            key={lane.name}
            lane={lane}
            laneTasks={tasksByLane[lane.name]}
            isTargetLane={targetLane === lane.name}
            isSourceLane={sourceLane === lane.name}
            defaultShortName={defaultShortName}
            onTaskChanged={onTaskChanged}
            onError={onError}
            onWorkstreamChanged={onWorkstreamChanged}
            onCreateTask={onCreateTask}
          />
        ))}
      </div>
      <DragOverlay>
        {activeTask ? (
          <TaskCard
            task={activeTask}
            defaultShortName={defaultShortName}
            onTaskChanged={onTaskChanged}
            onError={onError}
            onWorkstreamChanged={onWorkstreamChanged}
            rootStyle={{
              cursor: 'grabbing',
              boxShadow: '0 8px 16px rgba(0,0,0,0.18)',
            }}
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
